// @ts-strict-ignore
import React, { createContext, useCallback, useContext, useReducer } from "react";

import { ConfirmationModalWithIntent } from "./ConfirmationModalWithIntent";
import { ConfirmationModalWithNoIntent } from "./ConfirmationModalWithNoIntent";
import type { ConfirmationModalState, ConfirmationModalActions, Confirm, ConfirmationModalProps } from "./types";

const initialState: ConfirmationModalState = {
  isOpen: false,
  heading: "",
  body: "Please confirm",
  okText: "OK",
  cancelText: "Cancel",
  tertiaryText: "Cancel",
  intent: undefined,
  size: "sm",
  onOk: () => true,
  onCancel: () => false,
};

const reducer = (state: ConfirmationModalState, action: ConfirmationModalActions): ConfirmationModalState => {
  switch (action.type) {
    case "OPEN_CONFIRMATION_MODAL":
      return {
        ...state,
        isOpen: true,
        body: action.message ?? initialState.body,
        heading: action.options?.heading ?? initialState.heading,
        okText: action.options?.okText ?? initialState.okText,
        cancelText: action.options?.cancelText ?? initialState.cancelText,
        tertiaryText: action.options?.tertiaryText ?? initialState.tertiaryText,
        intent: action.options?.intent ?? initialState.intent,
        size: action.options?.size ?? initialState.size,
        onOk: action.options?.onOk ?? initialState.onOk,
        onCancel: action.options?.onCancel ?? initialState.onCancel,
        onTertiary: action.options?.onTertiary ?? initialState.onTertiary,
      };
    case "CLOSE_CONFIRMATION_MODAL":
      return initialState;
    default:
      return state;
  }
};

export const ConfirmationModalContext = createContext<Confirm>(() => null);

/**
 * This is the old confirmation modal that is used in the dashboard.
 * It is deprecated and will be removed in the future.
 *
 * @deprecated Use useAsyncConfirmationModal instead.
 */
export const useConfirmationModal = (): Confirm => {
  return useContext(ConfirmationModalContext);
};

/**
 * This is the old confirmation modal that is used in the dashboard.
 * It is deprecated and will be removed in the future.
 *
 * @deprecated Use AsyncConfirmationModalProvider instead.
 */
export const ConfirmationModal = ({ children }: ConfirmationModalProps) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const confirm: Confirm = useCallback(
    (message, options) => {
      // open the confirmation modal
      dispatch({ type: "OPEN_CONFIRMATION_MODAL", message, options });

      return {
        // skip calling onCancel and just close the modal since the calling component triggered this action
        closeConfirmation: () => dispatch({ type: "CLOSE_CONFIRMATION_MODAL" }),
      };
    },
    [dispatch]
  );

  const handleOk = () => {
    // resolve the promise
    state.onOk?.();

    // update state in reducer
    dispatch({ type: "CLOSE_CONFIRMATION_MODAL" });
  };

  const handleCancel = () => {
    // reject the promise
    state.onCancel?.();

    // update state in reducer
    dispatch({ type: "CLOSE_CONFIRMATION_MODAL" });
  };

  const handleTertiary = () => {
    // reject the promise
    state.onTertiary?.();

    // update state in reducer
    dispatch({ type: "CLOSE_CONFIRMATION_MODAL" });
  };

  return (
    <ConfirmationModalContext.Provider value={confirm}>
      {children}

      {state.intent ? (
        <ConfirmationModalWithIntent
          {...state}
          onCancel={handleCancel}
          onOk={handleOk}
          intent={state.intent}
          onTertiary={state.onTertiary ? handleTertiary : undefined}
        />
      ) : (
        <ConfirmationModalWithNoIntent
          {...state}
          onCancel={handleCancel}
          onOk={handleOk}
          onTertiary={state.onTertiary ? handleTertiary : undefined}
        />
      )}
    </ConfirmationModalContext.Provider>
  );
};
